import { buildWhatsAppApiSendUrlFromE164 } from "./buildWhatsAppApiSendUrl";
import { decorateSecondaryLabelForPublic } from "./decoratePublicLabels";
import { normalizeSecondaryButtons } from "./normalizeSecondaryButtons";
import { resolvePrimaryCta } from "./resolvePrimaryCta";
import {
  LINK_IN_BIO_DEFAULT_ACCENT_HEX,
  normalizeLinkInBioHexColor,
} from "./linkInBioTheme";

const PRIMARY_ALLOWED = new Set(["auto", "chat", "whatsapp", "order"]);

/**
 * @param {Record<string, unknown> | null | undefined} data
 * @param {(key: string, opts?: object) => string} t - i18n `linkInBioPublic`
 * @returns {{
 *   profile: object | null,
 *   primaryResolved: { kind: string, href: string } | null,
 *   primaryLabel: string | null,
 * }}
 */
export function buildProfileFromRpcData(data, t) {
  if (!data || typeof data !== "object") {
    return { profile: null, primaryResolved: null, primaryLabel: null };
  }

  const primaryCta = PRIMARY_ALLOWED.has(String(data.primary_cta))
    ? String(data.primary_cta)
    : "chat";

  const profile = {
    displayName: String(data.display_name || "").trim() || t("fallbackName"),
    tagline:
      typeof data.tagline === "string" && data.tagline.trim()
        ? data.tagline.trim()
        : null,
    logoUrl: typeof data.logo_url === "string" ? data.logo_url.trim() : "",
    themeLogoBg: normalizeLinkInBioHexColor(
      data.theme_logo_bg,
      LINK_IN_BIO_DEFAULT_ACCENT_HEX,
    ),
    themePrimaryBg: normalizeLinkInBioHexColor(
      data.theme_primary_bg,
      LINK_IN_BIO_DEFAULT_ACCENT_HEX,
    ),
    primaryCta,
    whatsappUrl: buildWhatsAppApiSendUrlFromE164(data.whatsapp_e164),
    chatUrl:
      typeof data.chat_url === "string" && data.chat_url.trim()
        ? data.chat_url.trim()
        : null,
    orderUrl:
      typeof data.order_url === "string" && data.order_url.trim()
        ? data.order_url.trim()
        : null,
    secondaries: normalizeSecondaryButtons(data.secondary_buttons).map(
      (btn) => ({
        ...btn,
        label: decorateSecondaryLabelForPublic(btn.type, btn.label),
      }),
    ),
  };

  const primaryResolved = resolvePrimaryCta({
    primaryCta: profile.primaryCta,
    chatUrl: profile.chatUrl,
    orderUrl: profile.orderUrl,
    whatsappUrl: profile.whatsappUrl,
  });

  const primaryLabel = primaryResolved
    ? t(`primary.${primaryResolved.kind}`)
    : null;

  return { profile, primaryResolved, primaryLabel };
}
